import type {
  ChapterCharacterLayer,
  CharacterEntity,
  CharacterLayerNote,
  CharacterOccurrence,
} from '../types/characterLayerTypes'
import type { KnownCharacterizationDevice } from '@variorum/character-index'
import { getCharacterOccurrenceReferentIds } from './characterLayerHelpers'

export type LectioEvidenceStanding = 'admitted' | 'received' | 'subjudice' | 'relegated'
export type LectioAdjudicationSourceKind = 'occurrence' | 'layer'
export type LectioAdjudicationEvidenceKind = 'name' | 'coref' | 'speaker' | 'focalization' | 'characterization'
export type LectioLayerKind = 'speaker' | 'focalization' | 'characterization'
export type LectioVoice = 'figure' | 'narrator' | 'communal'

export const LECTIO_FOCALIZATION_CLASSES = [
  'cognition',
  'perception',
  'affect',
  'narratorial evidential distance',
] as const

/**
 * The extraction pipeline's closed grammatical device grammar. These values
 * remain readable and editable as model evidence, but the Reader's manual menu
 * records the broader interpretive ontology declared below instead of
 * manufacturing a grammatical device judgment.
 */
export const LECTIO_CHARACTERIZATION_DEVICE_GROUPS = [
  {
    id: 'direct-predication',
    label: 'Direct predication',
    devices: [
      { id: 'naat', label: 'Attributive adjective (naʿt)' },
      { id: 'khabar', label: 'Nominal predicate (khabar)' },
      { id: 'kana_pred', label: 'Kāna predicate' },
      { id: 'kana_lahu', label: 'Possession of attribute (kāna lahu)' },
    ],
  },
  {
    id: 'portrait-comparison',
    label: 'Portrait and comparison',
    devices: [
      { id: 'body_part', label: 'Body-part portrait' },
      { id: 'tashbih', label: 'Comparison (tashbīh)' },
      { id: 'elative', label: 'Elative (afʿal al-tafḍīl)' },
      { id: 'tamyiz', label: 'Specification (tamyīz)' },
    ],
  },
  {
    id: 'state-manner-motive',
    label: 'State, manner, and motive',
    devices: [
      { id: 'hal', label: 'Circumstantial state (ḥāl)' },
      { id: 'maf3ul_mutlaq', label: 'Cognate manner accusative' },
      { id: 'maf3ul_liajlih', label: 'Motive accusative' },
      { id: 'manner_pp', label: 'Prepositional or adverbial manner' },
    ],
  },
  {
    id: 'explicit-evaluation',
    label: 'Explicit evaluation',
    devices: [
      { id: 'madh_dhamm', label: 'Praise or blame (madh/dhamm)' },
      { id: 'ta3ajjub', label: 'Exclamation (taʿajjub)' },
      { id: 'tasghir', label: 'Evaluative diminutive (taṣghīr)' },
      { id: 'dua', label: 'Invocation (duʿāʾ)' },
    ],
  },
  {
    id: 'descriptive-constructions',
    label: 'Descriptive constructions',
    devices: [
      { id: 'idafa_wasfiyya', label: 'Descriptive annexation (iḍāfa)' },
      { id: 'negated_trait', label: 'Negated trait' },
      { id: 'vocative_eval', label: 'Evaluative vocative' },
      { id: 'urf_reputation', label: 'Reputation (ʿurf)' },
      { id: 'naming', label: 'Characterizing naming' },
    ],
  },
  {
    id: 'action',
    label: 'Action',
    devices: [
      { id: 'habitual_kana', label: 'Habitual kāna' },
      { id: 'reaction', label: 'Reaction to figure' },
    ],
  },
] as const

export type LectioCharacterizationDevice = KnownCharacterizationDevice

export const LECTIO_CHARACTERIZATION_DEVICES: readonly LectioCharacterizationDevice[] = (
  LECTIO_CHARACTERIZATION_DEVICE_GROUPS.flatMap(group => group.devices.map(device => device.id))
)

const LECTIO_CHARACTERIZATION_DEVICE_SET = new Set<string>(LECTIO_CHARACTERIZATION_DEVICES)

export const isLectioCharacterizationDevice = (
  value: unknown,
): value is LectioCharacterizationDevice => (
  typeof value === 'string' && LECTIO_CHARACTERIZATION_DEVICE_SET.has(value)
)

export const normalizeLectioCharacterizationDevices = (
  values: readonly unknown[],
): LectioCharacterizationDevice[] => Array.from(new Set(values.filter(isLectioCharacterizationDevice)))

export const LECTIO_MANUAL_CHARACTERIZATION_DIRECT_FORMS = [
  { id: 'explicit_ascription', label: 'Explicit ascription' },
  { id: 'description_portrait', label: 'Description / portrait' },
  { id: 'comparison_exemplum', label: 'Comparison / exemplum' },
  { id: 'naming_epithet', label: 'Naming / epithet' },
] as const

export const LECTIO_MANUAL_CHARACTERIZATION_NARRATORIAL_FORMS = [
  ...LECTIO_MANUAL_CHARACTERIZATION_DIRECT_FORMS,
  { id: 'reputation', label: 'Reputation' },
] as const

export const LECTIO_MANUAL_CHARACTERIZATION_INDIRECT_CHANNELS = [
  { id: 'target_speech', label: "Through the target's speech" },
  { id: 'target_thought', label: "Through the target's thought" },
  { id: 'action_conduct', label: 'Through action / conduct' },
  { id: 'appearance_gesture', label: 'Through appearance / gesture' },
  { id: 'others_reaction', label: "Through another's reaction" },
  { id: 'narratorial_selection', label: 'Through narratorial selection' },
] as const

export const LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS = [
  { id: 'single_marked_action', label: 'Single marked action' },
  { id: 'habitual_repeated', label: 'Habitual / repeated' },
  { id: 'manner_motive', label: 'Manner or motive' },
] as const

export const LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS = [
  { id: 'speech_reaction', label: 'Speech reaction' },
  { id: 'emotional_bodily_reaction', label: 'Emotional / bodily reaction' },
  { id: 'behavioral_reaction', label: 'Behavioral reaction' },
] as const

export const LECTIO_MANUAL_CHARACTERIZATION_MODIFIERS = [
  { id: 'negated', label: 'Negated' },
  { id: 'habitual_persistent', label: 'Habitual / persistent' },
  { id: 'momentary_state', label: 'Momentary state' },
  { id: 'transformative_changing', label: 'Transformative / changing' },
  { id: 'praise', label: 'Praise' },
  { id: 'blame', label: 'Blame' },
] as const

export type LectioManualCharacterizationMode = 'direct' | 'indirect'
export type LectioManualCharacterizationDirectSource = 'self' | 'other' | 'narratorial'
export type LectioManualCharacterizationChannel = 'spoken' | 'interior'
export type LectioManualCharacterizationForm = (
  typeof LECTIO_MANUAL_CHARACTERIZATION_NARRATORIAL_FORMS[number]['id']
)
export type LectioManualCharacterizationIndirectChannel = (
  typeof LECTIO_MANUAL_CHARACTERIZATION_INDIRECT_CHANNELS[number]['id']
)
export type LectioManualCharacterizationIndirectDetail = (
  typeof LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS[number]['id']
  | typeof LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS[number]['id']
)
export type LectioManualCharacterizationModifier = (
  typeof LECTIO_MANUAL_CHARACTERIZATION_MODIFIERS[number]['id']
)

export interface LectioManualCharacterization {
  mode: LectioManualCharacterizationMode
  directSource?: LectioManualCharacterizationDirectSource
  channel?: LectioManualCharacterizationChannel
  sourceCharacterId?: string
  forms?: readonly LectioManualCharacterizationForm[]
  indirectChannel?: LectioManualCharacterizationIndirectChannel
  indirectDetail?: LectioManualCharacterizationIndirectDetail
  relatedCharacterId?: string
  modifiers?: readonly LectioManualCharacterizationModifier[]
}

export const LECTIO_SPEAKER_ADDRESS_MODES = [
  { id: 'inward', label: 'Inward / self-address' },
  { id: 'individual', label: 'One addressee' },
  { id: 'collective', label: 'Defined group' },
  { id: 'general', label: 'General audience' },
  { id: 'indeterminate', label: 'Addressee indeterminate' },
] as const

export const LECTIO_SPEAKER_DISCOURSE_FUNCTIONS = [
  { id: 'narrating', label: 'Narrating' },
  { id: 'reporting', label: 'Reporting' },
  { id: 'asking', label: 'Asking' },
  { id: 'answering', label: 'Answering' },
  { id: 'advising', label: 'Advising' },
  { id: 'commanding', label: 'Commanding' },
  { id: 'requesting', label: 'Requesting' },
  { id: 'warning', label: 'Warning' },
  { id: 'persuading', label: 'Persuading' },
  { id: 'evaluating_judging', label: 'Evaluating / judging' },
  { id: 'accusing_rebuking', label: 'Accusing / rebuking' },
  { id: 'deliberating_reasoning', label: 'Deliberating / reasoning' },
  { id: 'promising_committing', label: 'Promising / committing' },
  { id: 'threatening', label: 'Threatening' },
  { id: 'remembering_reminding', label: 'Remembering / reminding' },
  { id: 'correcting_retracting', label: 'Correcting / retracting' },
  { id: 'agreeing_confirming', label: 'Agreeing / confirming' },
  { id: 'disagreeing_refusing', label: 'Disagreeing / refusing' },
] as const

export const LECTIO_SPEAKER_DISCOURSE_FORMS = [
  { id: 'maxim', label: 'Maxim / wisdom saying' },
  { id: 'proverb', label: 'Proverb' },
  { id: 'embedded_narrative', label: 'Narrative / embedded story' },
  { id: 'exemplum', label: 'Exemplum' },
  { id: 'simile', label: 'Simile' },
  { id: 'metaphor', label: 'Metaphor' },
  { id: 'hyperbole', label: 'Hyperbole' },
  { id: 'analogy', label: 'Analogy' },
  { id: 'rhetorical_question', label: 'Rhetorical question' },
  { id: 'reported_speech', label: 'Quotation / reported speech' },
  { id: 'invocation', label: 'Prayer / curse / invocation' },
] as const

export const LECTIO_SPEAKER_ORIENTATIONS = [
  { id: 'self', label: 'Self' },
  { id: 'addressee', label: 'Addressee' },
  { id: 'other_character', label: 'Other character' },
  { id: 'group', label: 'Group' },
  { id: 'story_world', label: 'Story world' },
  { id: 'prior_utterance', label: 'Prior utterance' },
  { id: 'prior_event', label: 'Prior event' },
  { id: 'future_action', label: 'Future action' },
  { id: 'general_truth', label: 'General truth' },
  { id: 'hypothetical', label: 'Hypothetical / counterfactual' },
] as const

export const LECTIO_SPEAKER_STANCES = [
  { id: 'present_perception', label: 'Present perception' },
  { id: 'recollection', label: 'Recollection' },
  { id: 'retrospective_reinterpretation', label: 'Retrospective reinterpretation' },
  { id: 'anticipation', label: 'Anticipation' },
  { id: 'planning_intention', label: 'Planning / intention' },
  { id: 'inference', label: 'Inference' },
  { id: 'belief', label: 'Belief' },
  { id: 'uncertainty', label: 'Doubt / uncertainty' },
  { id: 'hearsay', label: 'Hearsay' },
  { id: 'recognition', label: 'Recognition / discovery' },
] as const

export type LectioSpeakerAddressMode = typeof LECTIO_SPEAKER_ADDRESS_MODES[number]['id']
export type LectioSpeakerDiscourseFunction = typeof LECTIO_SPEAKER_DISCOURSE_FUNCTIONS[number]['id']
export type LectioSpeakerDiscourseForm = typeof LECTIO_SPEAKER_DISCOURSE_FORMS[number]['id']
export type LectioSpeakerOrientation = typeof LECTIO_SPEAKER_ORIENTATIONS[number]['id']
export type LectioSpeakerStance = typeof LECTIO_SPEAKER_STANCES[number]['id']
export type LectioSpeakerRetrospectiveStatus = 'none' | 'unresolved' | 'linked'
export type LectioSpeakerRetrospectiveTargetKind = 'speech-turn' | 'event' | 'span'

export interface LectioSpeakerRetrospectiveTarget {
  kind: LectioSpeakerRetrospectiveTargetKind
  targetId?: string
  witnessId?: string
  rowId?: string
  endRowId?: string
  start?: number
  end?: number
  label?: string
}

export interface LectioSpeakerAnalysis {
  addressMode: LectioSpeakerAddressMode
  addresseeCharacterIds: readonly string[]
  functions: readonly LectioSpeakerDiscourseFunction[]
  forms: readonly LectioSpeakerDiscourseForm[]
  orientations: readonly LectioSpeakerOrientation[]
  stances: readonly LectioSpeakerStance[]
  retrospectiveStatus: LectioSpeakerRetrospectiveStatus
  retrospectiveTargets: readonly LectioSpeakerRetrospectiveTarget[]
}

export const LECTIO_VOICES = ['figure', 'narrator', 'communal'] as const

export type LectioFocalizationClass = typeof LECTIO_FOCALIZATION_CLASSES[number]
export type LectioLayerClassification = LectioFocalizationClass | LectioCharacterizationDevice

export interface LectioAdjudicationTarget {
  sourceKind: LectioAdjudicationSourceKind
  evidenceId: string
}

interface LectioAdjudicationTargetedCommand extends LectioAdjudicationTarget {
  op: 'admit' | 'relegate' | 'reclass' | 'voice' | 'remove'
}

export interface LectioAdjudicationClassifyOccurrenceCommand extends LectioAdjudicationTarget {
  op: 'classify-occurrence'
  sourceKind: 'occurrence'
  mentionType: 'name' | 'coref'
}

export interface LectioAdjudicationClassifyLayerCommand extends LectioAdjudicationTarget {
  op: 'classify-layer'
  sourceKind: 'layer'
  layerKind: LectioLayerKind
  classification?: LectioLayerClassification
  devices?: readonly LectioCharacterizationDevice[]
  manualCharacterization?: LectioManualCharacterization
  speakerAnalysis?: LectioSpeakerAnalysis
  voice: LectioVoice
}

export interface LectioAdjudicationResizeCommand extends LectioAdjudicationTarget {
  op: 'resize'
  /* Absolute bounds make a drag commit idempotent and independent of the removed
     incremental +/- controls. Application still clamps them into the source row. */
  start: number
  end: number
  sourceText: string
}

export interface LectioAdjudicationClaimCommand extends LectioAdjudicationTarget {
  op: 'claim'
  characterId: string
}

export interface LectioAdjudicationToggleSharedReferentCommand extends LectioAdjudicationTarget {
  op: 'toggle-shared-referent'
  sourceKind: 'occurrence'
  characterId: string
}

export interface LectioAdjudicationMergeCharacterCommand extends LectioAdjudicationTarget {
  op: 'merge-character'
  sourceKind: 'occurrence'
  sourceCharacterId: string
  characterId: string
}

export interface LectioAdjudicationAddCommand extends LectioAdjudicationTarget {
  op: 'add'
  characterId: string
  evidenceKind: LectioAdjudicationEvidenceKind
  witnessId: string
  rowId: string
  start: number
  end: number
  sourceText: string
  classification?: LectioLayerClassification
  devices?: readonly LectioCharacterizationDevice[]
  manualCharacterization?: LectioManualCharacterization
  speakerAnalysis?: LectioSpeakerAnalysis
  voice?: LectioVoice
  /** Stable across the row-sized fragments that make up one speech turn. */
  speechTurnId?: string
  /** Reserved for quotation within quotation; the first implementation uses level zero. */
  speechLevel?: number
  speechFragmentIndex?: number
  speechFragmentCount?: number
}

export interface LectioAdjudicationSession {
  characterId: string
  openingCharacterId: string
  characters: CharacterEntity[]
  occurrences: CharacterOccurrence[]
  notes: CharacterLayerNote[]
  touched: LectioAdjudicationTarget[]
  mergedCharacterIds: string[]
}

export interface LectioAdjudicationRestoreCommand {
  op: 'restore'
  session: LectioAdjudicationSession
}

export type LectioAdjudicationCommand =
  | LectioAdjudicationTargetedCommand
  | LectioAdjudicationClassifyOccurrenceCommand
  | LectioAdjudicationClassifyLayerCommand
  | LectioAdjudicationResizeCommand
  | LectioAdjudicationClaimCommand
  | LectioAdjudicationToggleSharedReferentCommand
  | LectioAdjudicationMergeCharacterCommand
  | LectioAdjudicationAddCommand
  | LectioAdjudicationRestoreCommand

interface ApplyLectioAdjudicationOptions {
  reviewer?: string
  timestamp?: string
}

const isLectioStanding = (value: unknown): value is LectioEvidenceStanding => (
  value === 'admitted' || value === 'received' || value === 'subjudice' || value === 'relegated'
)

const asRecord = (value: unknown): Record<string, any> => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {}
)

const readKnownValue = <T extends string>(
  value: unknown,
  values: readonly T[],
): T | undefined => typeof value === 'string' && values.includes(value as T) ? value as T : undefined

const readKnownValues = <T extends string>(
  value: unknown,
  values: readonly T[],
): T[] => Array.isArray(value)
  ? Array.from(new Set(value.filter(candidate => (
      typeof candidate === 'string' && values.includes(candidate as T)
    )) as T[]))
  : []

export const readLectioManualCharacterization = (
  value: unknown,
): LectioManualCharacterization | null => {
  const record = asRecord(value)
  const mode = readKnownValue(record.mode, ['direct', 'indirect'] as const)
  if (!mode) {
    return null
  }
  const forms = readKnownValues(
    record.forms,
    LECTIO_MANUAL_CHARACTERIZATION_NARRATORIAL_FORMS.map(form => form.id),
  )
  const modifiers = readKnownValues(
    record.modifiers,
    LECTIO_MANUAL_CHARACTERIZATION_MODIFIERS.map(modifier => modifier.id),
  )
  return {
    mode,
    directSource: readKnownValue(record.direct_source, ['self', 'other', 'narratorial'] as const),
    channel: readKnownValue(record.channel, ['spoken', 'interior'] as const),
    sourceCharacterId: typeof record.source_character_id === 'string' ? record.source_character_id : undefined,
    forms,
    indirectChannel: readKnownValue(
      record.indirect_channel,
      LECTIO_MANUAL_CHARACTERIZATION_INDIRECT_CHANNELS.map(channel => channel.id),
    ),
    indirectDetail: readKnownValue(record.indirect_detail, [
      ...LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS.map(detail => detail.id),
      ...LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS.map(detail => detail.id),
    ]),
    relatedCharacterId: typeof record.related_character_id === 'string' ? record.related_character_id : undefined,
    modifiers,
  }
}

const uniqueStrings = <T extends string>(values: readonly T[] | undefined): T[] => (
  Array.from(new Set(values || []))
)

const serializeManualCharacterization = (
  manual: LectioManualCharacterization,
): Record<string, any> => ({
  mode: manual.mode,
  ...(manual.directSource ? { direct_source: manual.directSource } : {}),
  ...(manual.channel ? { channel: manual.channel } : {}),
  ...(manual.sourceCharacterId ? { source_character_id: manual.sourceCharacterId } : {}),
  ...(manual.forms ? { forms: uniqueStrings(manual.forms) } : {}),
  ...(manual.indirectChannel ? { indirect_channel: manual.indirectChannel } : {}),
  ...(manual.indirectDetail ? { indirect_detail: manual.indirectDetail } : {}),
  ...(manual.relatedCharacterId ? { related_character_id: manual.relatedCharacterId } : {}),
  ...(manual.modifiers ? { modifiers: uniqueStrings(manual.modifiers) } : {}),
})

const speakerAddressModeIds = LECTIO_SPEAKER_ADDRESS_MODES.map(option => option.id)
const speakerFunctionIds = LECTIO_SPEAKER_DISCOURSE_FUNCTIONS.map(option => option.id)
const speakerFormIds = LECTIO_SPEAKER_DISCOURSE_FORMS.map(option => option.id)
const speakerOrientationIds = LECTIO_SPEAKER_ORIENTATIONS.map(option => option.id)
const speakerStanceIds = LECTIO_SPEAKER_STANCES.map(option => option.id)
const speakerRetrospectiveStatuses = ['none', 'unresolved', 'linked'] as const
const speakerRetrospectiveTargetKinds = ['speech-turn', 'event', 'span'] as const

const readSpeakerRetrospectiveTarget = (value: unknown): LectioSpeakerRetrospectiveTarget | null => {
  const record = asRecord(value)
  const kind = readKnownValue(record.kind, speakerRetrospectiveTargetKinds)
  const targetId = typeof record.target_id === 'string' && record.target_id.trim()
    ? record.target_id.trim()
    : undefined
  const rowId = typeof record.row_id === 'string' && record.row_id.trim()
    ? record.row_id.trim()
    : undefined
  if (!kind || (!targetId && !rowId)) {
    return null
  }
  return {
    kind,
    ...(targetId ? { targetId } : {}),
    ...(typeof record.witness_id === 'string' && record.witness_id.trim()
      ? { witnessId: record.witness_id.trim() }
      : {}),
    ...(rowId ? { rowId } : {}),
    ...(typeof record.end_row_id === 'string' && record.end_row_id.trim()
      ? { endRowId: record.end_row_id.trim() }
      : {}),
    ...(Number.isFinite(record.start) ? { start: Math.max(0, Math.floor(record.start)) } : {}),
    ...(Number.isFinite(record.end) ? { end: Math.max(0, Math.floor(record.end)) } : {}),
    ...(typeof record.label === 'string' && record.label.trim() ? { label: record.label.trim() } : {}),
  }
}

export const readLectioSpeakerAnalysis = (value: unknown): LectioSpeakerAnalysis => {
  const record = asRecord(value)
  const addressMode = readKnownValue(record.address_mode, speakerAddressModeIds) || 'indeterminate'
  const addresseeCharacterIds = addressMode === 'individual' || addressMode === 'collective'
    ? Array.from(new Set(Array.isArray(record.addressee_character_ids)
      ? record.addressee_character_ids
          .filter((candidate): candidate is string => typeof candidate === 'string' && Boolean(candidate.trim()))
          .map(candidate => candidate.trim())
      : []))
    : []
  const retrospectiveTargets = Array.isArray(record.retrospective_targets)
    ? record.retrospective_targets
        .map(readSpeakerRetrospectiveTarget)
        .filter((target): target is LectioSpeakerRetrospectiveTarget => Boolean(target))
    : []
  const requestedStatus = readKnownValue(record.retrospective_status, speakerRetrospectiveStatuses) || 'none'
  const retrospectiveStatus = retrospectiveTargets.length > 0 ? 'linked' : requestedStatus === 'linked'
    ? 'unresolved'
    : requestedStatus
  return {
    addressMode,
    addresseeCharacterIds,
    functions: readKnownValues(record.functions, speakerFunctionIds),
    forms: readKnownValues(record.forms, speakerFormIds),
    orientations: readKnownValues(record.orientations, speakerOrientationIds),
    stances: readKnownValues(record.stances, speakerStanceIds),
    retrospectiveStatus,
    retrospectiveTargets,
  }
}

const serializeSpeakerRetrospectiveTarget = (
  target: LectioSpeakerRetrospectiveTarget,
): Record<string, any> => ({
  kind: target.kind,
  ...(target.targetId ? { target_id: target.targetId } : {}),
  ...(target.witnessId ? { witness_id: target.witnessId } : {}),
  ...(target.rowId ? { row_id: target.rowId } : {}),
  ...(target.endRowId ? { end_row_id: target.endRowId } : {}),
  ...(Number.isFinite(target.start) ? { start: Math.max(0, Math.floor(target.start || 0)) } : {}),
  ...(Number.isFinite(target.end) ? { end: Math.max(0, Math.floor(target.end || 0)) } : {}),
  ...(target.label ? { label: target.label.trim() } : {}),
})

export const serializeLectioSpeakerAnalysis = (
  analysis: LectioSpeakerAnalysis,
): Record<string, any> => {
  const normalized = readLectioSpeakerAnalysis({
    address_mode: analysis.addressMode,
    addressee_character_ids: analysis.addresseeCharacterIds,
    functions: analysis.functions,
    forms: analysis.forms,
    orientations: analysis.orientations,
    stances: analysis.stances,
    retrospective_status: analysis.retrospectiveStatus,
    retrospective_targets: analysis.retrospectiveTargets.map(serializeSpeakerRetrospectiveTarget),
  })
  return {
    schema_version: 'variorum.speaker-analysis.v1',
    address_mode: normalized.addressMode,
    addressee_character_ids: [...normalized.addresseeCharacterIds],
    functions: [...normalized.functions],
    forms: [...normalized.forms],
    orientations: [...normalized.orientations],
    stances: [...normalized.stances],
    retrospective_status: normalized.retrospectiveStatus,
    retrospective_targets: normalized.retrospectiveTargets.map(serializeSpeakerRetrospectiveTarget),
  }
}

export const getLectioSpeakerAnalysisValidationError = (
  analysis: LectioSpeakerAnalysis,
): string => {
  if (analysis.addressMode === 'individual' && analysis.addresseeCharacterIds.length !== 1) {
    return 'Choose one addressee'
  }
  if (analysis.addressMode === 'collective' && analysis.addresseeCharacterIds.length === 0) {
    return 'Choose at least one member of the addressed group'
  }
  const retrospectiveReading = analysis.orientations.some(value => (
    value === 'prior_utterance' || value === 'prior_event'
  )) || analysis.stances.some(value => (
    value === 'recollection' || value === 'retrospective_reinterpretation'
  ))
  if (retrospectiveReading && analysis.retrospectiveStatus === 'none') {
    return 'Link the prior speech or event, or mark its target unresolved'
  }
  if (analysis.retrospectiveStatus === 'linked' && analysis.retrospectiveTargets.length === 0) {
    return 'Choose at least one retrospective target'
  }
  return ''
}

const getManualCharacterizationSource = (
  manual: LectioManualCharacterization,
  targetCharacterId: string,
): Record<string, any> => {
  if (manual.mode === 'direct') {
    if (manual.directSource === 'narratorial') {
      return { voice: 'narrator', level: 0 }
    }
    return {
      voice: 'figure',
      figure: manual.directSource === 'other'
        ? manual.sourceCharacterId || ''
        : targetCharacterId,
      channel: manual.channel === 'interior' ? 'interior' : 'speech',
    }
  }
  if (manual.indirectChannel === 'target_speech' || manual.indirectChannel === 'target_thought') {
    return {
      voice: 'figure',
      figure: targetCharacterId,
      channel: manual.indirectChannel === 'target_thought' ? 'interior' : 'speech',
    }
  }
  return { voice: 'narrator', level: 0 }
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const cycleValue = <T extends string>(values: readonly T[], current: unknown): T => {
  const index = typeof current === 'string' ? values.indexOf(current as T) : -1
  return values[(index + 1 + values.length) % values.length] || values[0]
}

const clamp = (value: number, minimum: number, maximum: number): number => (
  Math.max(minimum, Math.min(maximum, value))
)

const editorialMetadata = (
  metadata: Record<string, any> | undefined,
  standing: LectioEvidenceStanding,
  reviewer: string,
  timestamp: string,
): Record<string, any> => ({
  ...(metadata || {}),
  editorial_standing: standing,
  editorial_origin: 'human',
  editorial_review: {
    reviewed_at: timestamp,
    reviewed_by: reviewer,
  },
})

export const getLectioOccurrenceStanding = (occurrence: CharacterOccurrence): LectioEvidenceStanding => {
  const explicit = occurrence.metadata?.editorial_standing
  if (isLectioStanding(explicit)) {
    return explicit
  }
  if (occurrence.status === 'rejected') {
    return 'relegated'
  }
  return occurrence.status === 'confirmed' || occurrence.status === 'edited' ? 'admitted' : 'received'
}

export const getLectioLayerStanding = (note: CharacterLayerNote): LectioEvidenceStanding => {
  const explicit = note.metadata?.editorial_standing
  if (isLectioStanding(explicit)) {
    return explicit
  }
  return note.metadata?.editorial_origin === 'human' ? 'admitted' : 'received'
}

export const beginLectioAdjudicationSession = (
  layer: ChapterCharacterLayer,
  characterId: string,
): LectioAdjudicationSession => ({
  characterId,
  openingCharacterId: characterId,
  characters: clone(layer.characters),
  occurrences: clone(layer.occurrences),
  notes: clone(layer.notes),
  touched: [],
  mergedCharacterIds: [],
})

const getNoteCharacterId = (note: CharacterLayerNote): string => {
  if (note.anchor.kind === 'character') {
    return note.anchor.characterId
  }
  return typeof note.metadata?.character_id === 'string' ? note.metadata.character_id : ''
}

export const restoreLectioAdjudicationSession = (
  layer: ChapterCharacterLayer,
  session: LectioAdjudicationSession,
  timestamp = new Date().toISOString(),
): ChapterCharacterLayer => {
  const mergedCharacterIds = new Set(session.mergedCharacterIds || [])
  const occurrenceIds = new Set(
    session.touched.filter(item => item.sourceKind === 'occurrence').map(item => item.evidenceId),
  )
  const noteIds = new Set(
    session.touched.filter(item => item.sourceKind === 'layer').map(item => item.evidenceId),
  )
  const mergedOccurrenceIds = new Set(
    session.occurrences
      .filter(occurrence => getCharacterOccurrenceReferentIds(occurrence).some(id => mergedCharacterIds.has(id)))
      .map(occurrence => occurrence.occurrence_id),
  )
  const mergedNoteIds = new Set(
    session.notes
      .filter(note => mergedCharacterIds.has(getNoteCharacterId(note)))
      .map(note => note.note_id),
  )
  return {
    ...layer,
    characters: mergedCharacterIds.size === 0
      ? layer.characters
      : [
          ...layer.characters.filter(character => !mergedCharacterIds.has(character.character_id)),
          ...clone(session.characters.filter(character => mergedCharacterIds.has(character.character_id))),
        ],
    occurrences: [
      ...layer.occurrences.filter(occurrence => (
        !occurrenceIds.has(occurrence.occurrence_id) &&
        !getCharacterOccurrenceReferentIds(occurrence).some(id => mergedCharacterIds.has(id)) &&
        !mergedOccurrenceIds.has(occurrence.occurrence_id)
      )),
      ...clone(session.occurrences.filter(occurrence => (
        occurrenceIds.has(occurrence.occurrence_id) ||
        getCharacterOccurrenceReferentIds(occurrence).some(id => mergedCharacterIds.has(id))
      ))),
    ],
    notes: [
      ...layer.notes.filter(note => (
        !noteIds.has(note.note_id) &&
        !mergedCharacterIds.has(getNoteCharacterId(note)) &&
        !mergedNoteIds.has(note.note_id)
      )),
      ...clone(session.notes.filter(note => (
        noteIds.has(note.note_id) || mergedCharacterIds.has(getNoteCharacterId(note))
      ))),
    ],
    updated_at: timestamp,
  }
}

const occurrenceIdentityKey = (occurrence: CharacterOccurrence): string => {
  const mentionType = occurrence.metadata?.mention_type || occurrence.metadata?.mentionType || ''
  const { witnessId, rowId, localStart, localEnd } = occurrence.locator
  return [witnessId, rowId, localStart ?? '', localEnd ?? '', mentionType].join('|')
}

const occurrenceRefersToCharacter = (
  occurrence: CharacterOccurrence,
  characterId: string,
): boolean => getCharacterOccurrenceReferentIds(occurrence).includes(characterId)

const replaceOccurrenceReferent = (
  occurrence: CharacterOccurrence,
  sourceCharacterId: string,
  receiverCharacterId: string,
): CharacterOccurrence => {
  const currentReferents = getCharacterOccurrenceReferentIds(occurrence)
  if (!currentReferents.includes(sourceCharacterId)) {
    return occurrence
  }
  const characterId = occurrence.character_id === sourceCharacterId
    ? receiverCharacterId
    : occurrence.character_id
  const referentCharacterIds = Array.from(new Set([
    characterId,
    ...currentReferents.map(id => id === sourceCharacterId ? receiverCharacterId : id),
  ]))
  const nextOccurrence: CharacterOccurrence = {
    ...occurrence,
    character_id: characterId,
    metadata: { ...(occurrence.metadata || {}) },
  }
  if (referentCharacterIds.length > 1) {
    nextOccurrence.referent_character_ids = referentCharacterIds
    nextOccurrence.metadata = { ...nextOccurrence.metadata, reference_mode: 'joint' }
  } else {
    delete nextOccurrence.referent_character_ids
    delete nextOccurrence.metadata?.reference_mode
  }
  return nextOccurrence
}

const noteIdentityKey = (note: CharacterLayerNote): string => {
  const metadata = note.metadata || {}
  return JSON.stringify({
    anchor: note.anchor,
    layer: metadata.character_index_layer || '',
    class: metadata.class || '',
    devices: Array.isArray(metadata.devices) ? metadata.devices : [],
    text: note.text,
  })
}

const mergeCharacterIdentity = (
  layer: ChapterCharacterLayer,
  command: LectioAdjudicationMergeCharacterCommand,
  reviewer: string,
  timestamp: string,
): ChapterCharacterLayer => {
  const source = layer.characters.find(character => character.character_id === command.sourceCharacterId)
  const receiver = layer.characters.find(character => character.character_id === command.characterId)
  if (!source || !receiver || source.character_id === receiver.character_id) {
    return layer
  }

  const receiverMerge = asRecord(receiver.metadata?.identity_merge)
  const mergedFrom = Array.isArray(receiverMerge.merged_from)
    ? receiverMerge.merged_from.filter((value: unknown): value is string => typeof value === 'string')
    : []
  const aliases = Array.from(new Set([
    ...(receiver.aliases || []),
    source.display_name,
    ...(source.aliases || []),
  ].map(value => value.trim()).filter(value => value && value !== receiver.display_name)))
  const characters = layer.characters.map(character => {
    if (character.character_id === source.character_id) {
      return {
        ...character,
        status: 'rejected' as const,
        origin: 'manual' as const,
        metadata: {
          ...editorialMetadata(character.metadata, 'relegated', reviewer, timestamp),
          merged_into: receiver.character_id,
          merge_trigger_occurrence_id: command.evidenceId,
        },
        review: { reviewed_at: timestamp, reviewed_by: reviewer },
        updated_at: timestamp,
      }
    }
    if (character.character_id === receiver.character_id) {
      return {
        ...character,
        aliases,
        status: 'edited' as const,
        origin: 'manual' as const,
        metadata: {
          ...editorialMetadata(character.metadata, 'admitted', reviewer, timestamp),
          identity_merge: {
            ...receiverMerge,
            merged_from: Array.from(new Set([...mergedFrom, source.character_id])),
            trigger_occurrence_id: command.evidenceId,
          },
        },
        review: { reviewed_at: timestamp, reviewed_by: reviewer },
        updated_at: timestamp,
      }
    }
    return character
  })

  const seenOccurrences = new Map<string, string>()
  layer.occurrences.forEach(occurrence => {
    if (
      occurrenceRefersToCharacter(occurrence, receiver.character_id) &&
      !occurrenceRefersToCharacter(occurrence, source.character_id) &&
      getLectioOccurrenceStanding(occurrence) !== 'relegated'
    ) {
      seenOccurrences.set(occurrenceIdentityKey(occurrence), occurrence.occurrence_id)
    }
  })
  const occurrences = layer.occurrences.map(occurrence => {
    if (!occurrenceRefersToCharacter(occurrence, source.character_id)) {
      return occurrence
    }
    const remappedOccurrence = replaceOccurrenceReferent(
      occurrence,
      source.character_id,
      receiver.character_id,
    )
    const sourceWasPrimary = occurrence.character_id === source.character_id
    const wasRelegated = getLectioOccurrenceStanding(occurrence) === 'relegated'
    if (!sourceWasPrimary) {
      return {
        ...remappedOccurrence,
        status: wasRelegated ? 'rejected' as const : 'edited' as const,
        origin: 'manual' as const,
        metadata: {
          ...editorialMetadata(
            remappedOccurrence.metadata,
            wasRelegated ? 'relegated' : 'admitted',
            reviewer,
            timestamp,
          ),
          merged_referent_from_character_id: source.character_id,
          merged_referent_into_character_id: receiver.character_id,
        },
        review: { reviewed_at: timestamp, reviewed_by: reviewer },
        updated_at: timestamp,
      }
    }
    const key = occurrenceIdentityKey(remappedOccurrence)
    const duplicateOf = seenOccurrences.get(key)
    if (!duplicateOf && !wasRelegated) {
      seenOccurrences.set(key, remappedOccurrence.occurrence_id)
    }
    return {
      ...remappedOccurrence,
      status: duplicateOf || wasRelegated ? 'rejected' as const : 'edited' as const,
      origin: 'manual' as const,
      metadata: {
        ...editorialMetadata(
          remappedOccurrence.metadata,
          duplicateOf || wasRelegated ? 'relegated' : 'admitted',
          reviewer,
          timestamp,
        ),
        claimed_from_character_id: source.character_id,
        claimed_for_character_id: receiver.character_id,
        ...(duplicateOf ? { merged_duplicate_of: duplicateOf } : {}),
      },
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
  })

  const seenNotes = new Map<string, string>()
  layer.notes.forEach(note => {
    if (getNoteCharacterId(note) === receiver.character_id && getLectioLayerStanding(note) !== 'relegated') {
      seenNotes.set(noteIdentityKey(note), note.note_id)
    }
  })
  const notes = layer.notes.map(note => {
    if (getNoteCharacterId(note) !== source.character_id) {
      return note
    }
    const key = noteIdentityKey(note)
    const duplicateOf = seenNotes.get(key)
    const wasRelegated = getLectioLayerStanding(note) === 'relegated'
    if (!duplicateOf && !wasRelegated) {
      seenNotes.set(key, note.note_id)
    }
    return {
      ...note,
      anchor: note.anchor.kind === 'character'
        ? { kind: 'character' as const, characterId: receiver.character_id }
        : note.anchor,
      metadata: {
        ...editorialMetadata(
          note.metadata,
          duplicateOf || wasRelegated ? 'relegated' : 'admitted',
          reviewer,
          timestamp,
        ),
        character_id: receiver.character_id,
        claimed_from_character_id: source.character_id,
        claimed_for_character_id: receiver.character_id,
        ...(duplicateOf ? { merged_duplicate_of: duplicateOf } : {}),
      },
      updated_at: timestamp,
    }
  })

  return { ...layer, characters, occurrences, notes, updated_at: timestamp }
}

const updateOccurrence = (
  occurrence: CharacterOccurrence,
  command: Exclude<LectioAdjudicationCommand, LectioAdjudicationRestoreCommand | LectioAdjudicationAddCommand>,
  reviewer: string,
  timestamp: string,
): CharacterOccurrence => {
  if (command.op === 'admit') {
    return {
      ...occurrence,
      status: 'confirmed',
      origin: 'manual',
      metadata: editorialMetadata(occurrence.metadata, 'admitted', reviewer, timestamp),
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
  }
  if (command.op === 'relegate') {
    return {
      ...occurrence,
      status: 'rejected',
      origin: 'manual',
      metadata: editorialMetadata(occurrence.metadata, 'relegated', reviewer, timestamp),
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
  }
  if (command.op === 'resize') {
    if (command.sourceText.length === 0) {
      return occurrence
    }
    const nextStart = clamp(Math.floor(command.start), 0, command.sourceText.length - 1)
    const nextEnd = clamp(Math.floor(command.end), nextStart + 1, command.sourceText.length)
    return {
      ...occurrence,
      locator: { ...occurrence.locator, localStart: nextStart, localEnd: nextEnd },
      label: command.sourceText.slice(nextStart, nextEnd),
      status: 'edited',
      origin: 'manual',
      metadata: editorialMetadata(occurrence.metadata, 'admitted', reviewer, timestamp),
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
  }
  if (command.op === 'reclass') {
    const currentMentionType = occurrence.metadata?.mention_type || occurrence.metadata?.mentionType
    const mentionType = currentMentionType === 'name' ? 'coref' : 'name'
    return {
      ...occurrence,
      status: 'edited',
      origin: 'manual',
      metadata: {
        ...editorialMetadata(occurrence.metadata, 'admitted', reviewer, timestamp),
        mention_type: mentionType,
      },
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
  }
  if (command.op === 'classify-occurrence') {
    return {
      ...occurrence,
      status: 'edited',
      origin: 'manual',
      metadata: {
        ...editorialMetadata(occurrence.metadata, 'admitted', reviewer, timestamp),
        mention_type: command.mentionType,
      },
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
  }
  if (command.op === 'claim') {
    const nextOccurrence: CharacterOccurrence = {
      ...occurrence,
      character_id: command.characterId,
      status: 'edited',
      origin: 'manual',
      metadata: editorialMetadata(occurrence.metadata, 'admitted', reviewer, timestamp),
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
    delete nextOccurrence.referent_character_ids
    delete nextOccurrence.metadata?.reference_mode
    return nextOccurrence
  }
  if (command.op === 'toggle-shared-referent') {
    if (!command.characterId || command.characterId === occurrence.character_id) {
      return occurrence
    }
    const currentReferents = getCharacterOccurrenceReferentIds(occurrence)
    const nextReferents = currentReferents.includes(command.characterId)
      ? currentReferents.filter(characterId => characterId !== command.characterId)
      : [...currentReferents, command.characterId]
    const nextOccurrence: CharacterOccurrence = {
      ...occurrence,
      status: 'edited',
      origin: 'manual',
      metadata: editorialMetadata(occurrence.metadata, 'admitted', reviewer, timestamp),
      review: { reviewed_at: timestamp, reviewed_by: reviewer },
      updated_at: timestamp,
    }
    if (nextReferents.length > 1) {
      nextOccurrence.referent_character_ids = nextReferents
      nextOccurrence.metadata = {
        ...nextOccurrence.metadata,
        reference_mode: 'joint',
      }
    } else {
      delete nextOccurrence.referent_character_ids
      delete nextOccurrence.metadata?.reference_mode
    }
    return nextOccurrence
  }
  return occurrence
}

const updateLayerNote = (
  note: CharacterLayerNote,
  command: Exclude<LectioAdjudicationCommand, LectioAdjudicationRestoreCommand | LectioAdjudicationAddCommand>,
  reviewer: string,
  timestamp: string,
): CharacterLayerNote => {
  const metadata = note.metadata || {}
  if (command.op === 'admit' || command.op === 'relegate') {
    return {
      ...note,
      metadata: editorialMetadata(
        metadata,
        command.op === 'admit' ? 'admitted' : 'relegated',
        reviewer,
        timestamp,
      ),
      updated_at: timestamp,
    }
  }
  if (command.op === 'resize') {
    if (note.anchor.kind !== 'span') {
      return note
    }
    if (command.sourceText.length === 0) {
      return note
    }
    const nextStart = clamp(Math.floor(command.start), 0, command.sourceText.length - 1)
    const nextEnd = clamp(Math.floor(command.end), nextStart + 1, command.sourceText.length)
    const nextText = command.sourceText.slice(nextStart, nextEnd)
    const nextMetadata = editorialMetadata(metadata, 'admitted', reviewer, timestamp)
    if (metadata.character_index_layer === 'focalization') {
      if (metadata.source_field === 'marker') {
        nextMetadata.marker = nextText
      } else {
        nextMetadata.scope = nextText
      }
    } else {
      nextMetadata.span = nextText
    }
    return {
      ...note,
      anchor: {
        kind: 'span',
        locator: { ...note.anchor.locator, localStart: nextStart, localEnd: nextEnd },
      },
      metadata: nextMetadata,
      updated_at: timestamp,
    }
  }
  if (command.op === 'reclass') {
    if (metadata.character_index_layer === 'speaker') {
      return note
    }
    const nextMetadata = editorialMetadata(metadata, 'admitted', reviewer, timestamp)
    if (metadata.character_index_layer === 'focalization') {
      nextMetadata.class = cycleValue(LECTIO_FOCALIZATION_CLASSES, metadata.class)
    } else {
      const devices = normalizeLectioCharacterizationDevices(
        Array.isArray(metadata.devices) ? metadata.devices : [],
      )
      nextMetadata.devices = [cycleValue(LECTIO_CHARACTERIZATION_DEVICES, devices[0]), ...devices.slice(1)]
    }
    return { ...note, metadata: nextMetadata, updated_at: timestamp }
  }
  if (command.op === 'voice') {
    if (metadata.character_index_layer === 'speaker') {
      return note
    }
    const source = asRecord(metadata.source)
    const nextVoice = cycleValue(LECTIO_VOICES, source.voice)
    return {
      ...note,
      metadata: {
        ...editorialMetadata(metadata, 'admitted', reviewer, timestamp),
        source: nextVoice === 'narrator' ? { voice: 'narrator', level: 0 } : { voice: nextVoice },
      },
      updated_at: timestamp,
    }
  }
  if (command.op === 'classify-layer') {
    const nextMetadata = editorialMetadata(metadata, 'admitted', reviewer, timestamp)
    nextMetadata.character_index_layer = command.layerKind
    if (command.layerKind === 'speaker') {
      nextMetadata.speech_turn_id = typeof metadata.speech_turn_id === 'string'
        ? metadata.speech_turn_id
        : note.note_id
      nextMetadata.speech_level = typeof metadata.speech_level === 'number' ? metadata.speech_level : 0
      nextMetadata.span = typeof metadata.span === 'string' ? metadata.span : note.text
      nextMetadata.source_field = 'span'
      nextMetadata.speaker_analysis = serializeLectioSpeakerAnalysis(
        command.speakerAnalysis || readLectioSpeakerAnalysis(metadata.speaker_analysis),
      )
      delete nextMetadata.class
      delete nextMetadata.devices
      delete nextMetadata.manual_characterization
      delete nextMetadata.source
      return { ...note, metadata: nextMetadata, updated_at: timestamp }
    }
    nextMetadata.source = command.manualCharacterization
      ? getManualCharacterizationSource(
          command.manualCharacterization,
          typeof metadata.character_id === 'string' ? metadata.character_id : '',
        )
      : command.voice === 'narrator'
        ? { voice: 'narrator', level: 0 }
        : { voice: command.voice }
    if (command.layerKind === 'focalization') {
      nextMetadata.class = command.classification || LECTIO_FOCALIZATION_CLASSES[0]
      delete nextMetadata.devices
      delete nextMetadata.manual_characterization
    } else {
      if (command.manualCharacterization) {
        nextMetadata.manual_characterization = serializeManualCharacterization(command.manualCharacterization)
      } else {
        const devices = normalizeLectioCharacterizationDevices([
          ...(command.devices || []),
          command.classification,
        ])
        nextMetadata.devices = devices.length > 0 ? devices : [LECTIO_CHARACTERIZATION_DEVICES[0]]
      }
      delete nextMetadata.class
    }
    return { ...note, metadata: nextMetadata, updated_at: timestamp }
  }
  if (command.op === 'claim') {
    return {
      ...note,
      anchor: note.anchor.kind === 'character'
        ? { kind: 'character', characterId: command.characterId }
        : note.anchor,
      metadata: {
        ...editorialMetadata(metadata, 'admitted', reviewer, timestamp),
        character_id: command.characterId,
      },
      updated_at: timestamp,
    }
  }
  return note
}

const addReading = (
  layer: ChapterCharacterLayer,
  command: LectioAdjudicationAddCommand,
  timestamp: string,
): ChapterCharacterLayer => {
  const start = clamp(Math.floor(command.start), 0, command.sourceText.length)
  const end = clamp(Math.floor(command.end), start + 1, command.sourceText.length)
  const text = command.sourceText.slice(start, end)
  if (!text || end <= start) {
    return layer
  }
  if (command.sourceKind === 'occurrence') {
    const occurrence: CharacterOccurrence = {
      occurrence_id: command.evidenceId,
      character_id: command.characterId,
      locator: { witnessId: command.witnessId, rowId: command.rowId, localStart: start, localEnd: end },
      role_labels: ['mentioned'],
      label: text,
      status: 'confirmed',
      origin: 'manual',
      metadata: {
        mention_type: command.evidenceKind === 'name' ? 'name' : 'coref',
        editorial_standing: 'admitted',
        editorial_origin: 'human',
      },
      created_at: timestamp,
      updated_at: timestamp,
    }
    return { ...layer, occurrences: [...layer.occurrences, occurrence], updated_at: timestamp }
  }
  const kind: LectioLayerKind = command.evidenceKind === 'speaker'
    ? 'speaker'
    : command.evidenceKind === 'focalization'
      ? 'focalization'
      : 'characterization'
  const speechLevel = Number.isFinite(command.speechLevel)
    ? Math.max(0, Math.floor(command.speechLevel || 0))
    : 0
  const speakerConflictNoteIds = kind === 'speaker'
    ? layer.notes.filter(candidate => {
        if (
          candidate.anchor.kind !== 'span' ||
          candidate.metadata?.character_index_layer !== 'speaker' ||
          candidate.metadata?.character_id === command.characterId ||
          getLectioLayerStanding(candidate) === 'relegated'
        ) {
          return false
        }
        const candidateLevel = typeof candidate.metadata?.speech_level === 'number'
          ? candidate.metadata.speech_level
          : 0
        const locator = candidate.anchor.locator
        return candidateLevel === speechLevel &&
          locator.witnessId === command.witnessId &&
          locator.rowId === command.rowId &&
          (locator.localStart ?? Number.MAX_SAFE_INTEGER) < end &&
          (locator.localEnd ?? -1) > start
      }).map(candidate => candidate.note_id)
    : []
  const note: CharacterLayerNote = {
    note_id: command.evidenceId,
    anchor: {
      kind: 'span',
      locator: { witnessId: command.witnessId, rowId: command.rowId, localStart: start, localEnd: end },
    },
    text: `${kind} · ${text}`,
    metadata: {
      character_id: command.characterId,
      character_index_layer: kind,
      editorial_standing: speakerConflictNoteIds.length > 0 ? 'subjudice' : 'admitted',
      editorial_origin: 'human',
      ...(kind === 'speaker'
        ? {
            speech_turn_id: command.speechTurnId || command.evidenceId,
            speech_level: speechLevel,
            speech_fragment_index: Number.isFinite(command.speechFragmentIndex)
              ? Math.max(0, Math.floor(command.speechFragmentIndex || 0))
              : 0,
            speech_fragment_count: Number.isFinite(command.speechFragmentCount)
              ? Math.max(1, Math.floor(command.speechFragmentCount || 1))
              : 1,
            ...(speakerConflictNoteIds.length > 0 ? { speaker_conflict_note_ids: speakerConflictNoteIds } : {}),
            speaker_analysis: serializeLectioSpeakerAnalysis(
              command.speakerAnalysis || readLectioSpeakerAnalysis(undefined),
            ),
            span: text,
            source_field: 'span',
          }
        : {
            source: command.manualCharacterization
              ? getManualCharacterizationSource(command.manualCharacterization, command.characterId)
              : command.voice === 'narrator'
                ? { voice: 'narrator', level: 0 }
                : { voice: command.voice || 'figure' },
          }),
      ...(kind === 'focalization'
        ? {
            class: command.classification || LECTIO_FOCALIZATION_CLASSES[0],
            scope: text,
            source_field: 'scope',
          }
        : kind === 'characterization' ? {
            ...(command.manualCharacterization
              ? {
                  manual_characterization: serializeManualCharacterization(command.manualCharacterization),
                }
              : {
                  devices: (() => {
                    const devices = normalizeLectioCharacterizationDevices([
                      ...(command.devices || []),
                      command.classification,
                    ])
                    return devices.length > 0 ? devices : [LECTIO_CHARACTERIZATION_DEVICES[0]]
                  })(),
                }),
            span: text,
            source_field: 'span',
          } : {}),
    },
    created_at: timestamp,
    updated_at: timestamp,
  }
  return { ...layer, notes: [...layer.notes, note], updated_at: timestamp }
}

export const applyLectioAdjudicationCommand = (
  layer: ChapterCharacterLayer,
  command: LectioAdjudicationCommand,
  options: ApplyLectioAdjudicationOptions = {},
): ChapterCharacterLayer => {
  const reviewer = options.reviewer || 'local-user'
  const timestamp = options.timestamp || new Date().toISOString()
  if (command.op === 'restore') {
    return restoreLectioAdjudicationSession(layer, command.session, timestamp)
  }
  if (command.op === 'add') {
    return addReading(layer, command, timestamp)
  }
  if (command.op === 'merge-character') {
    return mergeCharacterIdentity(layer, command, reviewer, timestamp)
  }
  if (command.op === 'remove') {
    return command.sourceKind === 'occurrence'
      ? {
          ...layer,
          occurrences: layer.occurrences.filter(occurrence => occurrence.occurrence_id !== command.evidenceId),
          updated_at: timestamp,
        }
      : {
          ...layer,
          notes: layer.notes.filter(note => note.note_id !== command.evidenceId),
          updated_at: timestamp,
        }
  }
  if (command.sourceKind === 'occurrence') {
    return {
      ...layer,
      occurrences: layer.occurrences.map(occurrence => (
        occurrence.occurrence_id === command.evidenceId
          ? updateOccurrence(occurrence, command, reviewer, timestamp)
          : occurrence
      )),
      updated_at: timestamp,
    }
  }
  const targetNote = layer.notes.find(note => note.note_id === command.evidenceId)
  const targetSpeechTurnId = command.op === 'classify-layer' && command.layerKind === 'speaker' &&
    targetNote?.metadata?.character_index_layer === 'speaker' &&
    typeof targetNote.metadata.speech_turn_id === 'string'
      ? targetNote.metadata.speech_turn_id
      : ''
  return {
    ...layer,
    notes: layer.notes.map(note => (
      note.note_id === command.evidenceId || (
        Boolean(targetSpeechTurnId) &&
        note.metadata?.character_index_layer === 'speaker' &&
        note.metadata?.speech_turn_id === targetSpeechTurnId
      )
        ? updateLayerNote(note, command, reviewer, timestamp)
        : note
    )),
    updated_at: timestamp,
  }
}
